import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Divider,
  Paper,
  Fade,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useProject } from '../contexts/ProjectContext';
import { getProjectSettings, updateProjectSettings, getSettingsConfig, getLLMModels, getImageModels } from '../services/api';
import { useSearchParams, useNavigate } from 'react-router-dom';
import UserProfileSettings from '../components/UserProfileSettings';
import Subscription from './Subscription';
import { useAuth } from '../contexts/AuthContext';
import LogoutIcon from '@mui/icons-material/Logout';
import Usage from './Usage';

const TAB_MAP = {
  'preferences': 0,
  'profile': 1,
  'usage': 2,
  'billing': 3
};

const REVERSE_TAB_MAP = {
  0: 'preferences',
  1: 'profile',
  2: 'usage',
  3: 'billing'
};

const Settings = () => {
  const { selectedProject } = useProject();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [config, setConfig] = useState({ settings: {} });
  const [llmModels, setLLMModels] = useState([]);
  const [imageModels, setImageModels] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedSetting = searchParams.get('highlight');
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Initialize currentTab from URL or default to 0
  const activeTab = searchParams.get('active_tab');
  const [currentTab, setCurrentTab] = useState(TAB_MAP[activeTab] || 0);

  // Update URL when tab changes
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setSearchParams(params => {
      params.set('active_tab', REVERSE_TAB_MAP[newValue]);
      return params;
    });
  };

  useEffect(() => {
    if (selectedProject?.id) {
      loadSettings();
      loadConfig();
      loadLLMModels();
      loadImageModels();
    }
  }, [selectedProject]);

  // Update currentTab when URL changes
  useEffect(() => {
    const tab = searchParams.get('active_tab');
    if (tab && TAB_MAP[tab] !== undefined) {
      setCurrentTab(TAB_MAP[tab]);
    }
  }, [searchParams]);

  const loadSettings = async () => {
    try {
      const data = await getProjectSettings(selectedProject.id);
      console.log('Loaded settings from API:', data);
      setSettings(data || {});
    } catch (error) {
      console.error('Error loading settings:', error);
      setSnackbar({
        open: true,
        message: 'Error loading settings',
        severity: 'error'
      });
    }
  };

  const loadConfig = async () => {
    try {
      const data = await getSettingsConfig(selectedProject.id);
      console.log('Loaded config:', data);
      setConfig(data);
    } catch (error) {
      console.error('Error loading settings config:', error);
    }
  };

  const loadLLMModels = async () => {
    try {
      const data = await getLLMModels();
      const mappedModels = data.map(model => ({
        value: model.name,
        label: `${model.name} (${model.provider})`
      }));
      console.log('Loaded LLM models:', mappedModels);
      setLLMModels(mappedModels);
    } catch (error) {
      console.error('Error loading LLM models:', error);
    }
  };

  const loadImageModels = async () => {
    try {
      const data = await getImageModels();
      const mappedModels = data.map(model => ({
        value: model.name,
        label: `${model.name} (${model.provider})`
      }));
      console.log('Loaded image models:', mappedModels);
      setImageModels(mappedModels);
    } catch (error) {
      console.error('Error loading image models:', error);
    }
  };

  const handleSettingChange = async (key, value) => {
    console.log('Updating setting:', key, 'to value:', value);
    console.log('Current settings before update:', settings);
    
    const newSettings = { ...settings, [key]: value };
    console.log('New settings after update:', newSettings);
    
    try {
      const updatedSettings = await updateProjectSettings(selectedProject.id, newSettings);
      console.log('Settings updated in backend:', updatedSettings);
      setSettings(updatedSettings);
      setSnackbar({
        open: true,
        message: 'Settings updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      setSnackbar({
        open: true,
        message: 'Error updating settings',
        severity: 'error'
      });
    }
  };

  const renderSettingField = (setting) => {
    if (!settings) return null;
    
    console.log('Rendering setting:', setting.key, 'Current settings:', settings);
    const value = settings[setting.key];
    console.log('Current value for', setting.key, ':', value);

    const isHighlighted = setting.key === highlightedSetting;

    const fieldContent = (
      <Box sx={{ 
        p: 2, 
        bgcolor: isHighlighted ? 'primary.light' : 'transparent',
        borderRadius: 1,
        transition: 'background-color 0.3s'
      }}>
        {setting.type === 'select' ? (
          <FormControl fullWidth>
            <InputLabel>{setting.title}</InputLabel>
            <Select
              value={value || ''}
              label={setting.title}
              onChange={(e) => handleSettingChange(setting.key, e.target.value)}
              displayEmpty
            >
              {(setting.dynamic_options === 'llm_models' ? llmModels :
                setting.dynamic_options === 'image_models' ? imageModels :
                setting.options || []).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : setting.type === 'number' ? (
          <FormControl fullWidth>
            <TextField
              label={setting.title}
              type="number"
              value={value ?? ''}
              onChange={(e) => handleSettingChange(setting.key, parseFloat(e.target.value))}
              inputProps={{
                min: setting.min,
                max: setting.max,
                step: setting.step
              }}
            />
          </FormControl>
        ) : null}
      </Box>
    );

    return (
      <Fade key={setting.key} in={true} timeout={isHighlighted ? 1000 : 0}>
        <Box>
          {fieldContent}
          {setting.description && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', px: 2 }}>
              {setting.description}
            </Typography>
          )}
        </Box>
      </Fade>
    );
  };

  const renderSettingsByGroup = () => {
    if (!settings) return null;
    
    return Object.entries(config.settings || {}).map(([groupName, settings]) => (
      <Box key={groupName} sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', fontWeight: 'medium' }}>
          {groupName}
        </Typography>
        <Paper elevation={0} variant="outlined">
          <Stack spacing={3} sx={{ py: 3 }}>
            {settings.map(setting => renderSettingField(setting))}
          </Stack>
        </Paper>
      </Box>
    ));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error logging out. Please try again.',
        severity: 'error'
      });
    }
  };

  const renderContent = () => {
    switch (currentTab) {
      case 0: // Project Preferences
        return renderSettingsByGroup();
      case 1: // Profile Details
        return (
          <Box>
            <UserProfileSettings />
            <Box sx={{ mt: 4 }}>
              <Paper elevation={0} variant="outlined">
                <Stack spacing={2} sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                    Account Actions
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LogoutIcon />}
                    onClick={() => setLogoutDialogOpen(true)}
                  >
                    Log Out
                  </Button>
                </Stack>
              </Paper>
            </Box>
          </Box>
        );
      case 2: // Usage
        return <Usage />;
      case 3: // Plans and Billing
        return <Subscription />;
      default:
        return null;
    }
  };

  if (!settings && currentTab === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper 
        sx={{ 
          position: 'sticky', 
          top: 0,
          zIndex: 1,
          borderRadius: 0,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default'
        }}
        elevation={0}
      >
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          sx={{ minHeight: 48 }}
        >
          <Tab label="Project Preferences" sx={{ minHeight: 48 }} />
          <Tab label="Profile Details" sx={{ minHeight: 48 }} />
          <Tab label="Usage" sx={{ minHeight: 48 }} />
          <Tab label="Plans & Billing" sx={{ minHeight: 48 }} />
        </Tabs>
      </Paper>

      <Box sx={{ p: 3 }}>
        {renderContent()}
      </Box>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
      >
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to log out? You will need to sign in again to access your account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLogout} color="error" variant="contained">
            Log Out
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings; 